import React, { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, Link } from "react-router-dom";
import AdminPanel from "./components/AdminPanel";
import NewScanLayout from "./components/new-scan/NewScanLayout";
import SelectScansStep from "./components/new-scan/steps/SelectScansStep";
import SelectTargetsStep from "./components/new-scan/steps/SelectTargetsStep";
import ConfigureStep from "./components/new-scan/steps/ConfigureStep";
import ReviewStep from "./components/new-scan/steps/ReviewStep";
import ScanJobProgress from "./components/new-scan/ScanJobProgress";
import ManageToolsPage from "./components/admin-tools/ManageToolsPage";
import { NewScanWizardProvider } from "./components/new-scan/NewScanWizardProvider";
import { getCurrentUser, login } from "./api/auth";

function App() {
  const [credentials, setCredentials] = useState({ username: "", password: "" });
  const [auth, setAuth] = useState({
    token: localStorage.getItem("token"),
    role: localStorage.getItem("role")
  });
  const [error, setError] = useState("");
  const [checkingSession, setCheckingSession] = useState(Boolean(localStorage.getItem("token")));

  useEffect(() => {
    const expireSession = () => {
      setAuth({ token: null, role: null });
      setCheckingSession(false);
    };

    window.addEventListener("auth:expired", expireSession);

    if (!auth.token) {
      setCheckingSession(false);
      return () => window.removeEventListener("auth:expired", expireSession);
    }

    getCurrentUser()
      .then(user => setAuth(current => ({ ...current, role: user.role })))
      .catch(() => expireSession())
      .finally(() => setCheckingSession(false));

    return () => window.removeEventListener("auth:expired", expireSession);
  }, [auth.token]);

  const handleLogin = async (event) => {
    event.preventDefault();
    setError("");

    try {
      const session = await login(credentials.username, credentials.password);
      setAuth(session);
      setCredentials({ username: "", password: "" });
    } catch (err) {
      setError(err.response?.data?.error || "Login failed");
    }
  };

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("role");
    setAuth({ token: null, role: null });
  };

  if (checkingSession) {
    return (
      <main className="app-shell">
        <section className="auth-panel">
          <h1>CyberSec Portal</h1>
          <p className="muted-text">Checking secure session...</p>
        </section>
      </main>
    );
  }

  if (!auth.token) {
    return (
      <main className="app-shell">
        <section className="auth-panel">
          <h1>CyberSec Portal</h1>
          <form onSubmit={handleLogin} className="auth-form">
            <input
              value={credentials.username}
              onChange={(event) => setCredentials({ ...credentials, username: event.target.value })}
              placeholder="Username"
              autoComplete="username"
            />
            <input
              value={credentials.password}
              onChange={(event) => setCredentials({ ...credentials, password: event.target.value })}
              placeholder="Password"
              type="password"
              autoComplete="current-password"
            />
            <button type="submit">Login</button>
            {error && <p className="form-error">{error}</p>}
          </form>
        </section>
      </main>
    );
  }

  return (
    <BrowserRouter>
      <main className="app-shell">
        <header className="topbar">
          <div>
            <h1>CyberSec Dashboard</h1>
            <p>{auth.role === "admin" ? "Admin access" : "User access"}</p>
          </div>
          <nav className="topbar-nav" aria-label="Primary navigation">
            <Link to="/">Dashboard</Link>
            <Link to="/scans/new/scans">New Scan</Link>
            {auth.role === "admin" && <Link to="/admin/tools">Manage Tools</Link>}
          </nav>
          <button type="button" onClick={logout}>Logout</button>
        </header>

        <Routes>
          <Route path="/" element={<AdminPanel role={auth.role} />} />
          <Route
            path="/scans/new/*"
            element={
              <NewScanWizardProvider>
                <Routes>
                  <Route index element={<Navigate to="/scans/new/scans" replace />} />
                  <Route element={<NewScanLayout />}>
                    <Route path="scans" element={<SelectScansStep />} />
                    <Route path="targets" element={<SelectTargetsStep />} />
                    <Route path="configure" element={<ConfigureStep />} />
                    <Route path="review" element={<ReviewStep />} />
                  </Route>
                </Routes>
              </NewScanWizardProvider>
            }
          />
          <Route path="/scans/:jobId" element={<ScanJobProgress />} />
          <Route
            path="/admin/tools"
            element={auth.role === "admin" ? <ManageToolsPage /> : <Navigate to="/" replace />}
          />
        </Routes>
      </main>
    </BrowserRouter>
  );
}

export default App;
